import { FilestackExtensionService as IFilestackExtensionService } from "@smartsuite/smartdoc/lib/esm/extensions/filestack";
import { EditorView } from "@smartsuite/smartdoc/lib/esm/core/types";
import {
  AttachmentNodeAttrs,
  FilestackFile,
  ImageNodeAttrs,
  VideoNodeAttrs,
  UploadMode,
} from "@smartsuite/smartdoc/lib/esm/extensions/filestack/types";
import { Client, PickerFileMetadata, Security } from "filestack-js";
import {
  AllowedImageExtension,
  AnyFile,
  ForbiddenFileExtensions,
  ForbiddenFileMimeTypes,
  VideoExtensions,
} from "@smartsuite/types";

import { Platform } from "../../types";
import { getVideoURL } from "./video";

// SOURCE: frontend/frontend/projects/main/src/app/editor/extensions/filestack-extension.service.ts
const PICKER_SOURCES = [
  "local_file_system",
  "url",
  "webcam",
  "box",
  "dropbox",
  "googledrive",
  "onedrive",
  "onedriveforbusiness",
  "unsplash",
  "facebook",
  "instagram",
];

// SOURCE: frontend/frontend/projects/main/src/environments/default-environment.ts
const FILESTACK_MAX_SIZE = 5_000_000_000;

class FilestackExtensionService implements IFilestackExtensionService {
  constructor(
    private readonly client: Client,
    private readonly security: Security,
    private readonly platform: Platform
  ) {
    this.client.setSecurity(this.security);
  }

  getAttachmentUrl(attrs: AttachmentNodeAttrs): string {
    if (attrs.url) {
      return attrs.url;
    }

    return attrs?.file?.handle
      ? this.client.transform(attrs.file.handle, { security: this.security })
      : "";
  }

  getImageUrl(attrs: ImageNodeAttrs): string {
    if (attrs.url) {
      return attrs.url;
    }

    return attrs?.file?.handle
      ? this.client.transform(attrs.file.handle, {
          security: this.security,
          resize: { width: attrs.size.width },
        })
      : "";
  }

  getVideoUrl(attrs: VideoNodeAttrs): string {
    if (attrs.url) {
      return attrs.url;
    }

    const videoURL = getVideoURL(attrs, this.client, this.security, this.platform);

    return videoURL;
  }

  validateUploadingFile(file: File): boolean {
    if (file.size >= FILESTACK_MAX_SIZE) {
      window.alert(`File ${file.name} is too big.`);
      return false;
    }

    return true;
  }

  async uploadByUrl(url: string): Promise<FilestackFile> {
    this.client.setSecurity(this.security);

    const response = (await this.client.storeURL(
      url,
      undefined,
      undefined,
      this.security
    )) as PickerFileMetadata;

    return convertFile(response);
  }

  async uploadFile(file: File): Promise<FilestackFile> {
    if (isForbiddenFile(file)) {
      return await Promise.reject(new Error("Forbidden file type"));
    }

    this.client.setSecurity(this.security);

    const response = (await this.client.upload(
      file,
      undefined,
      undefined,
      undefined,
      this.security
    )) as PickerFileMetadata;

    return convertFile(response);
  }

  async openPicker(mode: UploadMode): Promise<FilestackFile[]> {
    this.client.setSecurity(this.security);

    const accept = getPickerAccept(mode);

    return await new Promise((resolve) => {
      let isSettled = false;
      let hasUploadStarted = false;

      const settle = (reason: string, files: FilestackFile[]): void => {
        if (isSettled) {
          return;
        }

        isSettled = true;
        resolve(files);
      };

      const picker = this.client.picker({
        accept,
        acceptFn: acceptFileFn,
        disableTransformer: true,
        fromSources: PICKER_SOURCES,
        maxFiles: 100,
        uploadInBackground: true,
        onUploadStarted: () => {
          hasUploadStarted = true;
        },
        onCancel: () => {
          settle("cancel", []);
        },
        onClose: () => {
          if (!hasUploadStarted) {
            settle("close-without-upload", []);
          }
        },
        onUploadDone: (response) => {
          const uploadedFiles = response.filesUploaded.map(convertFile);

          settle("upload-done", uploadedFiles);
        },
      });

      void picker.open();
    });
  }

  openModalGallery(view: EditorView, attrs: ImageNodeAttrs): void {
    void view;
    void attrs;
  }
}

function getPickerAccept(mode: UploadMode): string[] | undefined {
  const acceptMap: Record<string, string[] | undefined> = {
    video: VideoExtensions,
    image: AllowedImageExtension,
    default: AnyFile,
  };

  return acceptMap[mode] ?? acceptMap.default;
}

function convertFile(file: PickerFileMetadata): FilestackFile {
  return {
    handle: file.handle,
    metadata: {
      filename: file.filename,
      size: file.size,
      container: file.container ?? "",
      key: file.key ?? "",
      mimetype: file.mimetype,
    },
  };
}

function isPickerFileMetadata(file: File | PickerFileMetadata): file is PickerFileMetadata {
  const pickerFile = file as PickerFileMetadata;

  return pickerFile.handle !== undefined || pickerFile.source === "url";
}

function isForbiddenFile(file: File | PickerFileMetadata): boolean {
  const [fileName, mimeType] = isPickerFileMetadata(file)
    ? [file.filename, file.mimetype]
    : [file.name, file.type];
  const extension = fileName?.split(".").pop()?.toLowerCase() ?? "";

  return ForbiddenFileExtensions.includes(extension) || ForbiddenFileMimeTypes.includes(mimeType);
}

async function acceptFileFn(file: PickerFileMetadata): Promise<string> {
  if (isForbiddenFile(file)) {
    return await Promise.reject(new Error("Forbidden file type"));
  }

  return await Promise.resolve("ok");
}

export { FilestackExtensionService };
